import { useMemo } from "react";

import { keepPreviousData } from "@tanstack/react-query";

import { queryKeys } from "../queryKeys";
import { getPaperAccounts, getPaperAccountsWithDetails } from "../rest/account";
import useRequest, { doNotRetryOn401 } from "./useRequest";

/**
 * Query options accepted by this hook. Kept intentionally narrow and
 * data-type-agnostic: the two underlying queries return different shapes
 * (`PaperAccount[]` then `Account[]`), so exposing the full generic
 * `QueryOptions<T>` forced casts at the call sites. Callers only ever need to
 * toggle `enabled` / `refetchInterval`.
 */
interface UseGetPaperAccountsWithDetailsOptions {
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Returns the paper accounts with all the information as Accounts, instead of the
 * minimum information of PaperAccounts
 * @param options
 * @returns
 */
export const useGetPaperAccountsWithDetails = (
  options?: UseGetPaperAccountsWithDetailsOptions,
) => {
  const {
    data: paperAccounts,
    refetch,
    isLoading,
    error: paperAccountsError,
  } = useRequest(queryKeys.accounts.paper(), () => getPaperAccounts(), {
    ...options,
    // authoritative: not overridable by caller options
    retry: doNotRetryOn401,
  });

  const hasPaperAccounts = !!paperAccounts?.length;
  const paperAccountIDs = useMemo(
    () => (paperAccounts ?? []).map((p) => p.paper_account_id).sort(),
    [paperAccounts],
  );

  const {
    data: paperAccountsWithDetails,
    isLoading: isDetailLoading,
    error: paperAccountsDetailError,
  } = useRequest(
    queryKeys.accounts.paperDetail(paperAccountIDs),
    () => getPaperAccountsWithDetails(paperAccountIDs),
    {
      ...options,
      enabled: (options?.enabled ?? true) && hasPaperAccounts,
      // authoritative: not overridable by caller options
      retry: doNotRetryOn401,
      // Retain the previous ID set's details while a new set loads. Without this,
      // creating/removing a paper account changes the query key, briefly resetting
      // `data` to [] -> the derived `product` flaps to undefined -> account-scoped
      // queries misroute a paper id to the live `/accounts/:id` path -> 401 -> the
      // global interceptor logs the user out.
      placeholderData: keepPreviousData,
    },
  );

  // Memoized rather than `?? []` inline: this is the array AccountProvider
  // republishes through context, and an inline fallback would be a new array on
  // every render for as long as the detail query has no data, which defeats any
  // memoization the provider or its consumers try to do on top of it.
  const data = useMemo(
    () => paperAccountsWithDetails ?? [],
    [paperAccountsWithDetails],
  );

  return {
    data,
    paper_accounts: paperAccounts,
    isLoading: isLoading || isDetailLoading,
    refetch: refetch,
    error: paperAccountsError || paperAccountsDetailError,
  };
};

export default useGetPaperAccountsWithDetails;
